import { useEffect, useState } from "react";
import useSWR from "swr";
import { toast } from "sonner";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
} from "recharts";
import { Panel, PanelHeader, Kpi } from "@/components/common/Card";
import { getPairs, runBacktest, formatNum } from "@/lib/api";
import { BACKTEST } from "@/constants/testIds";
import {
  TrendUp,
  Lightning,
  Play,
  ChartLineUp,
  Trophy,
  Warning,
  Wallet,
  Target,
} from "@phosphor-icons/react";

const STRATEGIES = [
  { v: "trend_pullback", l: "Trend Pullback" },
  { v: "breakout_retest", l: "Breakout Retest" },
  { v: "range_trading", l: "Range Trading" },
  { v: "liquidity_sweep", l: "Liquidity Sweep" },
];

const TIMEFRAMES = [
  { v: "15m", l: "15 minutes" },
  { v: "1H", l: "1 hour" },
  { v: "4H", l: "4 hours" },
  { v: "1D", l: "1 day" },
];

const Label = ({ children, hint }) => (
  <div className="mb-1.5">
    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">
      {children}
    </div>
    {hint && <div className="text-[11px] text-slate-400 mt-0.5">{hint}</div>}
  </div>
);

const Select = ({ value, onChange, options, testid }) => (
  <select
    data-testid={testid}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="w-full bg-[#f1f5f9] border border-slate-200 hover:border-slate-300 text-slate-900 text-sm font-data rounded-sm px-3 py-2 focus:border-[#2563eb] focus:outline-none"
  >
    {options.map((o) => (
      <option key={o.v} value={o.v}>
        {o.l}
      </option>
    ))}
  </select>
);

const NumberInput = ({ value, onChange, testid, min, max, step }) => (
  <input
    data-testid={testid}
    type="number"
    min={min}
    max={max}
    step={step}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
    className="w-full bg-[#f1f5f9] border border-slate-200 hover:border-slate-300 text-slate-900 text-sm font-data rounded-sm px-3 py-2 focus:border-[#2563eb] focus:outline-none"
  />
);

const fmtPnl = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toFixed(2)}`;
};

const fmtTs = (t) => {
  if (!t) return "—";
  // Backtest timestamps are stored in seconds (epoch). Detect ms vs s.
  const ms = String(Math.floor(Number(t))).length > 10 ? Number(t) : Number(t) * 1000;
  try {
    return new Date(ms).toLocaleString(undefined, {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return "—";
  }
};

export const Backtest = () => {
  const { data: pairsData } = useSWR("/pairs?limit=100", () => getPairs(100));
  const pairs = pairsData?.pairs || [];

  const [pair, setPair] = useState("");
  const [strategy, setStrategy] = useState("trend_pullback");
  const [timeframe, setTimeframe] = useState("1H");
  const [balance, setBalance] = useState(1000);
  const [risk, setRisk] = useState(1.0);
  const [bars, setBars] = useState(500);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  // Auto-select first pair when loaded
  useEffect(() => {
    if (!pair && pairs.length > 0) setPair(pairs[0]);
  }, [pairs, pair]);

  const onRun = async () => {
    if (!pair) {
      toast.error("Pick a trading pair first");
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      const res = await runBacktest({
        pair,
        strategy,
        timeframe,
        starting_balance: Number(balance) || 1000,
        risk_per_trade: Number(risk) || 1.0,
        bars: Number(bars) || 500,
      });
      setResult(res);
      toast.success("Backtest complete", {
        description: `${res.total_trades} trades · ${res.win_rate}% win rate · PF ${res.profit_factor}`,
      });
    } catch (e) {
      toast.error("Backtest failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setRunning(false);
    }
  };

  const pnlReturn = result
    ? ((result.final_balance - result.starting_balance) / result.starting_balance) * 100
    : 0;
  const isProfit = pnlReturn >= 0;

  return (
    <div className="flex flex-col gap-6 max-w-[1600px]">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-slate-500 font-bold">
            Historical Simulator
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-black tracking-tighter text-slate-900 mt-1">
            Backtest
          </h1>
          <div className="text-xs text-slate-500 mt-2">
            Replay a strategy on WEEX historical candles · fixed 2R targets · 1.5×ATR stops.
          </div>
        </div>
      </div>

      {/* Inputs */}
      <Panel>
        <PanelHeader
          title={
            <span className="inline-flex items-center gap-2">
              <Lightning size={12} weight="bold" className="text-[#2563eb]" />
              Configuration
            </span>
          }
          hint="Choose pair, strategy and risk profile, then run the simulation."
        />
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 p-4 items-end">
          <div className="col-span-2 md:col-span-1">
            <Label>Pair</Label>
            <Select
              testid={BACKTEST.pairSelect}
              value={pair}
              onChange={setPair}
              options={
                pairs.length > 0
                  ? pairs.map((p) => ({ v: p, l: p }))
                  : [{ v: "", l: "Loading pairs…" }]
              }
            />
          </div>
          <div>
            <Label>Strategy</Label>
            <Select
              testid={BACKTEST.strategySelect}
              value={strategy}
              onChange={setStrategy}
              options={STRATEGIES.map((s) => ({ v: s.v, l: s.l }))}
            />
          </div>
          <div>
            <Label>Timeframe</Label>
            <Select
              testid={BACKTEST.timeframeSelect}
              value={timeframe}
              onChange={setTimeframe}
              options={TIMEFRAMES.map((t) => ({ v: t.v, l: t.l }))}
            />
          </div>
          <div>
            <Label hint="USDT starting equity">Balance</Label>
            <NumberInput
              testid={BACKTEST.balanceInput}
              value={balance}
              onChange={setBalance}
              min={100}
              max={1000000}
              step={100}
            />
          </div>
          <div>
            <Label hint="% per trade">Risk %</Label>
            <NumberInput
              testid={BACKTEST.riskInput}
              value={risk}
              onChange={setRisk}
              min={0.1}
              max={10}
              step={0.1}
            />
          </div>
          <div>
            <Label hint="Candles to simulate">Bars</Label>
            <NumberInput
              testid={BACKTEST.barsInput}
              value={bars}
              onChange={setBars}
              min={220}
              max={1000}
              step={50}
            />
          </div>
        </div>
        <div className="flex justify-end px-4 pb-4">
          <button
            data-testid={BACKTEST.runBtn}
            onClick={onRun}
            disabled={running || !pair}
            className="inline-flex items-center gap-2 bg-[#2563eb] hover:bg-[#2563eb]/90 disabled:opacity-50 text-white text-xs font-bold tracking-widest uppercase px-5 py-2.5 rounded-sm transition"
          >
            <Play size={14} weight="fill" />
            {running ? "Running…" : "Run Backtest"}
          </button>
        </div>
      </Panel>

      {/* Result */}
      {running && (
        <Panel className="p-10 text-center">
          <div className="inline-flex items-center gap-3 text-xs text-slate-600 font-data tracking-wider uppercase">
            <span className="h-2 w-2 rounded-full bg-[#2563eb] animate-pulse" />
            Replaying {bars} candles on {pair} · {strategy.replace("_", " ")}…
          </div>
        </Panel>
      )}

      {!running && result && (
        <div data-testid={BACKTEST.result} className="flex flex-col gap-6">
          {/* KPI grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            <Kpi
              testid="bt-kpi-total-trades"
              label="Total Trades"
              value={result.total_trades}
              sub={`${result.wins}W / ${result.losses}L`}
            />
            <Kpi
              testid="bt-kpi-win-rate"
              label="Win Rate"
              value={`${result.win_rate}%`}
              accent={result.win_rate >= 50 ? "text-[#16a34a]" : "text-[#dc2626]"}
            />
            <Kpi
              testid="bt-kpi-profit-factor"
              label="Profit Factor"
              value={result.profit_factor}
              accent={result.profit_factor >= 1 ? "text-[#16a34a]" : "text-[#dc2626]"}
            />
            <Kpi
              testid="bt-kpi-max-dd"
              label="Max Drawdown"
              value={`${result.max_drawdown}%`}
              accent="text-[#d97706]"
            />
            <Kpi
              testid="bt-kpi-avg-rr"
              label="Avg RR"
              value={`${result.avg_rr}R`}
              sub="fixed 2R targets"
            />
            <Kpi
              testid="bt-kpi-best"
              label="Best Trade"
              value={fmtPnl(result.best_trade)}
              accent="text-[#16a34a]"
            />
            <Kpi
              testid="bt-kpi-worst"
              label="Worst Trade"
              value={fmtPnl(result.worst_trade)}
              accent="text-[#dc2626]"
            />
            <Kpi
              testid="bt-kpi-final"
              label="Final Balance"
              value={formatNum(result.final_balance, 2)}
              accent={isProfit ? "text-[#16a34a]" : "text-[#dc2626]"}
              sub={`${isProfit ? "+" : ""}${pnlReturn.toFixed(2)}%`}
            />
          </div>

          {/* Equity Curve */}
          <Panel>
            <PanelHeader
              title={
                <span className="inline-flex items-center gap-2">
                  <ChartLineUp size={12} weight="bold" className="text-[#2563eb]" />
                  Equity Curve
                </span>
              }
              hint={`${result.pair} · ${result.strategy.replace("_", " ")} · ${result.timeframe}`}
              action={
                <span className="text-[10px] tracking-widest uppercase text-slate-500 font-data">
                  {result.equity_curve?.length || 0} pts
                </span>
              }
            />
            <div
              data-testid={BACKTEST.equityChart}
              className="p-4 h-[340px]"
            >
              {result.equity_curve && result.equity_curve.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={result.equity_curve}
                    margin={{ top: 8, right: 16, bottom: 8, left: 0 }}
                  >
                    <defs>
                      <linearGradient id="eq" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2563eb" stopOpacity={0.6} />
                        <stop offset="100%" stopColor="#2563eb" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#e2e8f0" vertical={false} />
                    <XAxis
                      dataKey="t"
                      tickFormatter={fmtTs}
                      tick={{ fill: "#64748b", fontSize: 10 }}
                      axisLine={{ stroke: "#cbd5e1" }}
                      tickLine={false}
                      minTickGap={48}
                    />
                    <YAxis
                      dataKey="balance"
                      tick={{ fill: "#64748b", fontSize: 10 }}
                      axisLine={{ stroke: "#cbd5e1" }}
                      tickLine={false}
                      width={64}
                      domain={["auto", "auto"]}
                    />
                    <Tooltip
                      contentStyle={{
                        background: "#ffffff",
                        border: "1px solid #e2e8f0",
                        borderRadius: 2,
                        fontFamily: "monospace",
                        fontSize: 11,
                      }}
                      labelFormatter={fmtTs}
                      formatter={(val) => [`${Number(val).toFixed(2)} USDT`, "Balance"]}
                    />
                    <ReferenceLine
                      y={result.starting_balance}
                      stroke="#64748b"
                      strokeDasharray="3 3"
                      label={{
                        value: `Start ${result.starting_balance}`,
                        position: "insideTopLeft",
                        fill: "#64748b",
                        fontSize: 10,
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="balance"
                      stroke={isProfit ? "#16a34a" : "#dc2626"}
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-xs text-slate-400">
                  No equity data available.
                </div>
              )}
            </div>
          </Panel>

          {/* Trade history */}
          <Panel>
            <PanelHeader
              title={
                <span className="inline-flex items-center gap-2">
                  <Target size={12} weight="bold" className="text-[#2563eb]" />
                  Trade History
                </span>
              }
              hint="Most recent 50 trades from this run"
              action={
                <span className="text-[10px] tracking-widest uppercase text-slate-500 font-data">
                  {result.trades?.length || 0} shown
                </span>
              }
            />
            <div data-testid={BACKTEST.trades} className="overflow-x-auto">
              <table className="w-full text-xs font-data">
                <thead>
                  <tr className="text-[10px] tracking-[0.1em] uppercase text-slate-500 border-b border-slate-200/70 bg-[#f1f5f9]">
                    <th className="text-left px-4 py-2 font-semibold">#</th>
                    <th className="text-left px-4 py-2 font-semibold">Entry Time</th>
                    <th className="text-left px-4 py-2 font-semibold">Exit Time</th>
                    <th className="text-left px-4 py-2 font-semibold">Side</th>
                    <th className="text-right px-4 py-2 font-semibold">Entry</th>
                    <th className="text-right px-4 py-2 font-semibold">Exit</th>
                    <th className="text-right px-4 py-2 font-semibold">SL %</th>
                    <th className="text-right px-4 py-2 font-semibold">PnL</th>
                    <th className="text-left px-4 py-2 font-semibold">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {(result.trades || []).length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center px-4 py-10 text-slate-400">
                        No trades were taken with these parameters.
                      </td>
                    </tr>
                  ) : (
                    [...result.trades].reverse().map((t, idx) => {
                      const win = t.result === "win";
                      return (
                        <tr
                          key={`${t.time}-${idx}`}
                          data-testid={`bt-trade-row-${idx}`}
                          className="border-b border-slate-200/70 hover:bg-[#101015]"
                        >
                          <td className="px-4 py-2 text-slate-500">{idx + 1}</td>
                          <td className="px-4 py-2 text-slate-700">{fmtTs(t.time)}</td>
                          <td className="px-4 py-2 text-slate-700">{fmtTs(t.exit_time)}</td>
                          <td className="px-4 py-2">
                            <span
                              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border text-[10px] tracking-widest uppercase ${
                                t.direction === "LONG"
                                  ? "text-[#16a34a] border-[#16a34a]/30 bg-[#16a34a]/5"
                                  : "text-[#dc2626] border-[#dc2626]/30 bg-[#dc2626]/5"
                              }`}
                            >
                              {t.direction}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right text-slate-700">{formatNum(t.entry)}</td>
                          <td className="px-4 py-2 text-right text-slate-700">{formatNum(t.exit)}</td>
                          <td className="px-4 py-2 text-right text-slate-600">
                            {t.sl_pct ? `${Number(t.sl_pct).toFixed(2)}%` : "—"}
                          </td>
                          <td
                            className={`px-4 py-2 text-right font-bold ${
                              win ? "text-[#16a34a]" : "text-[#dc2626]"
                            }`}
                          >
                            {fmtPnl(t.pnl)}
                          </td>
                          <td className="px-4 py-2">
                            <span
                              className={`inline-flex items-center gap-1 text-[10px] tracking-widest uppercase ${
                                win ? "text-[#16a34a]" : "text-[#dc2626]"
                              }`}
                            >
                              {win ? (
                                <Trophy size={11} weight="fill" />
                              ) : (
                                <Warning size={11} weight="fill" />
                              )}
                              {t.result}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>
      )}

      {!running && !result && (
        <Panel className="p-12 text-center">
          <TrendUp size={32} weight="duotone" className="mx-auto mb-3 text-slate-400" />
          <div className="text-sm text-slate-600">No backtest run yet</div>
          <div className="text-xs text-slate-400 mt-1">
            Configure parameters above and press <span className="text-[#2563eb]">Run Backtest</span>.
          </div>
        </Panel>
      )}
    </div>
  );
};

export default Backtest;
